// Which stock reels a pre-stranding phase can draw on, rope by rope.
//
// A cable with a reduced earth is stranded TWICE. CYYF 3x35+16 has a 35 mm²
// rope for the three live cores and a 16 mm² one for the green-yellow, and the
// wizard already draws them as two cards:
//
//   Rope 1 — 1×Ø2.14 + 6×Ø2.52     8 000 m / 8 000 m
//   Rope 2 — 1×Ø1.35 + 6×Ø1.7      2 000 m / 2 000 m
//
// The stock picker did not. It asked `/phantoms/compatible` once, with
// `section = product.section`, and that endpoint matches a pre-stranding
// phantom by wireType + SECTION (deliberately — see controller/phantoms.js: one
// section can be stranded from different gauges, so the diameters are not the
// identity). So with COPPER_ROPE_SOLID stock for both 1x35 and 1x16 on the
// floor, only the 35 came back and the 16 was never offered — reported
// 2026-10-22, and the reason the green-yellow rope could not be fed from stock
// at all.
//
// Two numbers differ per rope and both are in here, because getting either from
// the wrong rope is silent:
//
//   · the SECTION, which is what the query matches on;
//   · how much is NEEDED — the live cores take `length × conductors`, the
//     single earth takes `length`. Same rule as `getTargetLengthForRope` in the
//     wizard.
//
// Which rope is the earth is not labelled in the data: it is the one with the
// SMALLEST stranded area. That rule already exists twice in the wizard
// (`buildUnwindingSpecs`, `getTargetLengthForRope`); this is the same one, in a
// place a test can reach.

const num = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

/** Stranded area, mm² — Σ count × π(d/2)². Enough to rank two ropes. */
export function ropeArea(rope) {
  const diams = Array.isArray(rope?.wireDiameter) ? rope.wireDiameter : [];
  const counts = Array.isArray(rope?.numberOfWires) ? rope.numberOfWires : [];
  if (!diams.length) return 0;
  return diams.reduce((s, d, i) => {
    const r = num(d) / 2;
    return s + num(counts[i]) * Math.PI * r * r;
  }, 0);
}

/** "1×Ø2.14 + 6×Ø2.52" — the only thing that tells two ropes apart on screen. */
export function ropeStructure(rope) {
  const diams = Array.isArray(rope?.wireDiameter) ? rope.wireDiameter : [];
  const counts = Array.isArray(rope?.numberOfWires) ? rope.numberOfWires : [];
  if (!diams.length) return '';
  return diams.map((d, i) => `${num(counts[i])}×Ø${num(d)}`).join(' + ');
}

/**
 * One spec per rope of a pre-stranding phase.
 *
 * @param product { section, greenYellowSection, hasMixedConductorSections,
 *                  numberOfConductors, length }
 * @param phase   the pre_stranding phase — `rope[]` when mixed, flat otherwise
 * @returns [{ ropeIndex, isVG, label, structure, section, wireType, needed }]
 *          `ropeIndex: null` for a cable with one rope, which is what the
 *          wizard's execution rows carry for the unmixed case.
 */
export function preStrandingRopeSpecs(product = {}, phase = {}) {
  const length = num(product.length);
  const cores = num(product.numberOfConductors);
  const ropes = Array.isArray(phase?.rope) ? phase.rope : [];

  if (!ropes.length) {
    return [
      {
        ropeIndex: null,
        isVG: false,
        label: 'Rope',
        structure: ropeStructure(phase),
        section: product.section ?? null,
        wireType: phase?.type ?? null,
        needed: length > 0 && cores > 0 ? length * cores : length
      }
    ];
  }

  const areas = ropes.map(ropeArea);
  // The smallest is the earth. `reduce` rather than `Math.min` + `indexOf`:
  // two ropes of equal area (a cable with no reduced earth that still has two
  // rope entries) must pick ONE, not both.
  const vgIdx = areas.reduce((best, a, i) => (a < areas[best] ? i : best), 0);

  const gy = num(product.greenYellowSection, null);
  const hasGy = !!product.hasMixedConductorSections && gy > 0;

  return ropes.map((rope, ropeIndex) => {
    const isVG = ropeIndex === vgIdx && ropes.length > 1;
    return {
      ropeIndex,
      isVG,
      label: `Rope ${ropeIndex + 1} — ${isVG ? 'VG' : 'MAIN'}`,
      structure: ropeStructure(rope),
      // The earth's own section, when the product states one. Without it the
      // query would ask for the main section on both ropes and find the same
      // reel twice — which is the bug, from the other side.
      section: isVG && hasGy ? gy : (product.section ?? null),
      wireType: rope?.type ?? phase?.type ?? null,
      // One earth per cable, `cores` live wires.
      needed: isVG ? length : length * (cores || 1)
    };
  });
}

/**
 * What is actually on one incoming execution, for the next phase's card.
 *
 * `conductor_isolation` showed "Construction: 1 × Ø2.14 + 6 × Ø2.52" and
 * "Total drums: 7" — but only when the execution carried a `ropeIndex`, which
 * only a MIXED pre-stranding produces. A plain cable's rope, and every reel
 * taken from stock, arrived as a card with a machine and a length and nothing
 * about the conductor in it.
 *
 * Precedence, and the order matters:
 *   1. the REEL's own spec — a phantom is a real bobbin and knows what is wound
 *      on it; if it disagrees with the recipe, the bobbin is right;
 *   2. the rope it feeds, when the cable is stranded twice;
 *   3. the phase's single spec.
 *
 * @returns { structure, totalDrums, fromPhantom } or null when nothing says.
 */
export function incomingConstruction(exec, previousPhase) {
  const phantomSpec =
    exec?.isPhantomSource &&
    (Array.isArray(exec.phantomWireDiameter) || Array.isArray(exec.phantomNumberOfWires))
      ? {
          wireDiameter: exec.phantomWireDiameter,
          numberOfWires: exec.phantomNumberOfWires
        }
      : null;

  const rope =
    typeof exec?.ropeIndex === 'number' ? previousPhase?.rope?.[exec.ropeIndex] : null;

  const src = phantomSpec || rope || previousPhase || null;
  const structure = ropeStructure(src);
  if (!structure) return null;

  const counts = Array.isArray(src?.numberOfWires) ? src.numberOfWires : [];
  const totalDrums = counts.reduce((a, b) => a + num(b), 0);

  return {
    structure,
    totalDrums: totalDrums > 0 ? totalDrums : null,
    fromPhantom: !!phantomSpec
  };
}

const phantomRopeSpecs = {
  preStrandingRopeSpecs,
  ropeArea,
  ropeStructure,
  incomingConstruction
};
export default phantomRopeSpecs;
